import { forwardRef, MouseEvent, ReactNode, Ref } from 'react';
import cn from 'classnames';

import './list-item-input.scss';
import { ListItem, ListItemProps, ListItemText } from './ListItem';
import { TextProps } from '../../atoms/text/Text';

export interface ListItemInputProps extends ListItemProps {
  /**
   * The ID of the ListItemInput.
   */
  id?: string;
  /**
   * The children of the ListItemInput.
   */
  children?: ReactNode | ReactNode[];
  /**
   * Primary text for the ListItemInput.
   */
  primary?: string;
  /**
   * Props for the primary text
   * @default {}
   */
  primaryProps?: TextProps;
  /**
   * Secondary text for the ListItemInput.
   */
  secondary?: string;
  /**
   * Props for the secondary text
   * @default {}
   */
  secondaryProps?: TextProps;
  /**
   * Determines the position of the control.
   * @default 'left'
   */
  controlPosition?: 'left' | 'right';
  /**
   * Determines if the ListItemInput is selected.
   */
  isSelected?: boolean;
  /**
   * Determines if the halo around the input is displayed when selected.
   */
  showHalo?: boolean;
  /**
   * Callback function for when the ListItemInput is clicked.
   */
  handleClick?: (id: string) => void;
  /**
   * Additional classNames to add to the list item.
   * @default []
   */
  classNames?: string[];
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLLIElement>;
}

export const ListItemInput = forwardRef<HTMLLIElement, ListItemInputProps>(
  (
    {
      children,
      controlPosition = 'left',
      id,
      isSelected,
      primary,
      primaryProps,
      secondary,
      secondaryProps,
      showHalo,
      handleClick,
      classNames = [],
      ...props
    }: ListItemInputProps,
    ref: ListItemInputProps['ref'],
  ) => {
    const classLIInput = 'lsux-list-item-input';
    const cnListItemInput = cn({ [`${classLIInput}`]: true }, ...classNames);

    const classLIInputButton = `${classLIInput}__button`;
    const cnLIInputButton = cn({
      [`${classLIInputButton}`]: true,
      [`${classLIInputButton}--selected`]: isSelected,
      [`show-halo`]: showHalo,
    });

    const listItemText = (
      <ListItemText
        classNames={['lsux-list-item-input__text']}
        primary={primary}
        primaryProps={primaryProps}
        secondary={secondary}
        secondaryProps={secondaryProps}
        onClick={onClickHandler}
      />
    );

    function onClickHandler(e: MouseEvent<HTMLDivElement>) {
      e.preventDefault();
      handleClick?.(id);
    }

    return (
      <ListItem {...props} ref={ref} classNames={[cnListItemInput]}>
        <a className={cnLIInputButton}>
          {controlPosition === 'right' ? listItemText : null}
          <div className={cn(`${classLIInputButton}__children`, `${classLIInputButton}__children--${controlPosition}`)}>
            {children}
          </div>
          {controlPosition === 'left' ? listItemText : null}
        </a>
      </ListItem>
    );
  },
);

ListItemInput.displayName = 'ListItemInput';
